import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { partnersAPI } from '../../services/api';

const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchPartners();
  }, [navigate]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partnersAPI.getAll();
      if (response.records) {
        setPartners(response.records);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPartner(null);
    setShowModal(true);
  };

  const handleEdit = (partner: any) => {
    setEditingPartner(partner);
    setShowModal(true);
  };

  const handleDelete = async (partner: any) => {
    try {
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer le partenaire ${partner.name} ?`)) {
        await partnersAPI.delete(partner.id);
        setPartners(prev => prev.filter(item => item.id !== partner.id));
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Erreur lors de la suppression du partenaire');
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingPartner) {
        // Update existing partner
        const updatedData = { ...formData, id: editingPartner.id };
        await partnersAPI.update(updatedData);
        
        setPartners(prev => prev.map(item => 
          item.id === editingPartner.id ? { ...item, ...formData } : item
        ));
      } else {
        // Add new partner
        const response = await partnersAPI.create(formData);
        
        if (response.id) {
          const newPartner = {
            id: response.id,
            ...formData,
            created_at: new Date().toISOString()
          };
          setPartners(prev => [newPartner, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingPartner(null);
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Erreur lors de l\'enregistrement du partenaire');
    } finally {
      setFormLoading(false);
    }
  };

  const formFields = [
    {
      name: 'name',
      label: 'Nom',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom du partenaire'
    },
    {
      name: 'category',
      label: 'Catégorie',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'title', label: 'Partenaire Titre' },
        { value: 'official', label: 'Partenaire Officiel' },
        { value: 'equipment', label: 'Équipement' },
        { value: 'institutional', label: 'Institutionnel' },
        { value: 'support', label: 'Soutien' }
      ]
    },
    {
      name: 'logo_url',
      label: 'URL du logo',
      type: 'text' as const,
      placeholder: 'https://example.com/logo.jpg'
    },
    {
      name: 'website',
      label: 'Site web',
      type: 'text' as const,
      placeholder: 'example.com'
    },
    {
      name: 'partnership_since',
      label: 'Partenaire depuis',
      type: 'text' as const,
      placeholder: '2023'
    },
    {
      name: 'description_fr',
      label: 'Description (Français)',
      type: 'textarea' as const,
      placeholder: 'Description du partenaire en français',
      rows: 3
    },
    {
      name: 'description_en',
      label: 'Description (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Description du partenaire en anglais',
      rows: 3
    },
    {
      name: 'testimonial_fr',
      label: 'Témoignage (Français)',
      type: 'textarea' as const,
      placeholder: 'Témoignage du partenaire en français',
      rows: 3
    },
    {
      name: 'testimonial_en',
      label: 'Témoignage (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Témoignage du partenaire en anglais',
      rows: 3
    }
  ];

  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          {row.logo_url && (
            <img 
              src={row.logo_url} 
              alt={value}
              className="w-10 h-10 rounded object-cover mr-3"
            />
          )}
          <div className="font-medium text-gray-900">{value}</div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Catégorie',
      render: (value: string) => {
        const categories: {[key: string]: {label: string, color: string}} = {
          'title': { label: 'Titre', color: 'bg-gold text-yellow-800' },
          'official': { label: 'Officiel', color: 'bg-blue-100 text-blue-800' },
          'equipment': { label: 'Équipement', color: 'bg-green-100 text-green-800' },
          'institutional': { label: 'Institutionnel', color: 'bg-purple-100 text-purple-800' },
          'support': { label: 'Soutien', color: 'bg-gray-100 text-gray-800' }
        };
        
        const category = categories[value] || { label: value, color: 'bg-gray-100 text-gray-800' };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${category.color}`}>
            {category.label}
          </span>
        );
      }
    },
    {
      key: 'website',
      label: 'Site web',
      render: (value: string) => (
        <a href={`https://${value}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {value}
        </a>
      )
    },
    {
      key: 'partnership_since',
      label: 'Depuis',
      sortable: true
    },
    {
      key: 'description_fr',
      label: 'Description',
      render: (value: string) => (
        <div className="truncate max-w-xs" title={value}>
          {value}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des partenaires...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Gestion des Partenaires"
          data={partners}
          columns={columns}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingPartner(null);
          }}
          title={editingPartner ? 'Modifier le partenaire' : 'Nouveau partenaire'}
          size="lg"
        >
          <AdminForm
            fields={formFields}
            initialData={editingPartner || {}}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowModal(false);
              setEditingPartner(null);
            }}
            loading={formLoading}
          />
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminPartners;